'use client'

import { useTranslations } from 'next-intl'
import { ReactNode, useMemo } from 'react'

import { HedgeInstrumentSelect } from '@/features/hedge/components/hedge-instrument-select'
import {
  distinctPositionUnderlyings,
  hedgeInstrumentScope,
  resolveDefaultHedgeInstrument,
} from '@/features/hedge/lib/hedge-instrument'
import { HedgeFormModel } from '@/features/hedge/types'
import { cn } from '@/lib/utils'
import { formatSymbol } from '@/lib/utils/format-util'
import { Position } from '@/types/generated/common/trading'
import { Exchange, Instrument } from '@/types/generated/common/types'

interface HedgeInstrumentFieldProps {
  form: HedgeFormModel
  positions: Position[]
  // Open underlyings (from positions), used to preview a whole-portfolio hedge.
  openUnderlyings: string[]
  instrumentMap: Map<string, Instrument>
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
  error?: string
  onChange: (patch: Partial<HedgeFormModel>) => void
  // Host-form styling hooks; the dialog's compact layout is the default.
  className?: string
  labelClassName?: string
  fieldClassName?: string
}

// The hedge instrument is optional: by default the backend derives each scoped underlying's
// perpetual. An explicit override is only offered when the scope resolves to a single
// underlying (one-underlying mode or a single-underlying group), as a labeled "Hedge with"
// dropdown like the Target/Unit fields — it used to be the instrument name inside the summary
// sentence, which nobody could tell was clickable.
// instrumentMap/multiExchangeInstrumentMap come from the parent's explicitly-selected account
// (see use-hedge-builder.ts), not re-derived from the focused card.
export function HedgeInstrumentField({
  form,
  positions,
  openUnderlyings,
  instrumentMap,
  multiExchangeInstrumentMap,
  error,
  onChange,
  className,
  labelClassName,
  fieldClassName,
}: HedgeInstrumentFieldProps) {
  const t = useTranslations('trade.hedgeBuilder')
  const scope = hedgeInstrumentScope(form, positions, instrumentMap, multiExchangeInstrumentMap)
  // The explicit hedge instrument must be tradable by the selected account. The
  // multi-exchange map is still used to resolve position metadata, but must not populate
  // this picker with instruments from unrelated venues.
  const accountInstruments = useMemo(() => Array.from(instrumentMap.values()), [instrumentMap])

  const defaultPerp = scope.underlying
    ? resolveDefaultHedgeInstrument(accountInstruments, scope.underlying)
    : undefined
  const defaultPerpLabel = scope.underlying
    ? (defaultPerp?.symbol ?? `${scope.underlying} perpetual`)
    : ''
  const instrumentLabel = form.hedgeInstrument.trim() || defaultPerpLabel

  if (scope.underlying && scope.allowsExplicit) {
    return (
      <div className={cn('flex w-64 max-w-full flex-col gap-1.5', className)}>
        <span className={cn('text-foreground/50 text-xs', labelClassName)}>{t('hedgeWith')}</span>
        <HedgeInstrumentSelect
          value={form.hedgeInstrument}
          underlying={scope.underlying}
          onChange={(v) => onChange({ hedgeInstrument: v })}
          label={instrumentLabel}
          defaultLabel={defaultPerpLabel}
          instruments={accountInstruments}
          fieldClassName={fieldClassName}
        />
        {error && <p className="text-destructive text-xs">{error}</p>}
      </div>
    )
  }

  let body: ReactNode
  if (scope.underlying) {
    body = `Will hedge ${scope.underlying} with ${formatSymbol(instrumentLabel)}.`
  } else if (form.scopeMode === 'underlying') {
    body = t('selectUnderlyingForInstrument')
  } else if (form.scopeMode === 'portfolio') {
    body = openUnderlyings.length
      ? `Will hedge ${openUnderlyings.join(', ')} with their perpetuals.`
      : t('willHedgePortfolio')
  } else {
    const groupPositions = positions.filter((p) => form.positionSymbols.includes(p.symbol))
    const groupUnderlyings = distinctPositionUnderlyings(
      groupPositions,
      instrumentMap,
      multiExchangeInstrumentMap
    )
    body = groupUnderlyings.length
      ? `Will hedge ${groupUnderlyings.join(', ')} with their perpetuals.`
      : t('addPositions')
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="text-foreground/50 text-xs">{body}</p>
      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  )
}
